import { useRef, useState } from 'react';
import { api, descargarArchivo, ErrorApi } from '../servicios/api.js';
import { useSesion } from '../servicios/sesion.js';
import { useUbicaciones } from './selectores.js';
import { Aviso, Dialogo } from './piezas.js';

interface ErrorFilaImportacion {
  linea: number;
  columna: string;
  mensaje: string;
  valor?: string;
}

interface ImportacionDto {
  id: string;
  estado: 'VALIDANDO' | 'VALIDADA' | 'CONFIRMADA' | 'FALLIDA' | 'CANCELADA';
  filasTotales: number;
  filasValidas: number;
  errores: ErrorFilaImportacion[];
}

interface OmitidaImportacion {
  linea: number;
  nombre: string;
  motivo: string;
}

interface ProgresoImportacion {
  procesadas: number;
  total: number;
}

/** OT de GetMaintainX que no entraron, con el paso donde falló (log de la importación). */
interface FalloImportacion {
  linea: number;
  idExterno: string;
  titulo: string;
  paso: 'PREPARACION' | 'INSERCION' | 'CONTENIDO';
  motivo: string;
  fecha: string;
}

/** `GET /importaciones/:id`: la importación más el estado de su confirmación en segundo plano. */
interface EstadoConfirmacionDto extends ImportacionDto {
  progreso: ProgresoImportacion | null;
  confirmando: boolean;
  ultimoError: string | null;
  resultado: { creadas: string[]; omitidas: OmitidaImportacion[] } | null;
  fallos: FalloImportacion[];
}

const PASO_FALLO: Record<FalloImportacion['paso'], string> = {
  PREPARACION: 'preparación (antes de crear)',
  INSERCION: 'inserción (la OT se deshizo)',
  CONTENIDO: 'fotos o procedimiento (la OT sí se creó)',
};

/**
 * Botón "Importar CSV", visible solo para quien tiene `importacion:ejecutar`
 * (Administrador — §12.5). Flujo en dos pasos, igual que el resto del
 * sistema de importaciones (§14.4): primero se valida el archivo entero y se
 * muestra el informe de errores por fila sin escribir nada; solo si el
 * usuario confirma se crean los registros.
 */
export function BotonImportarCsv({
  ruta,
  etiqueta = 'Importar CSV',
  alTerminar,
}: {
  /** Segmento de `/importaciones/{ruta}/validar`, p. ej. "ubicaciones". */
  ruta: 'ubicaciones' | 'activos' | 'plantillas' | 'ordenes' | 'partes';
  etiqueta?: string;
  /** Se invoca tras confirmar con éxito, para refrescar el listado. */
  alTerminar: () => void;
}): JSX.Element | null {
  const sesion = useSesion();
  const [abierto, setAbierto] = useState(false);

  // § pedido: importar un CSV pisa datos existentes (o los borra del todo con
  // "Reemplazar") en Activos, Ubicaciones y Procedimientos — se restringe a
  // Administrador, no a cualquiera con el permiso de importación.
  if (!sesion.puede('importacion:ejecutar') || sesion.usuario?.tipoAcceso !== 'ADMINISTRADOR') return null;

  return (
    <>
      {/* Revisar un CSV de cientos de filas y su informe de errores no es una
          tarea de celular: se oculta en móvil, igual que el resto de los
          botones secundarios de estas cabeceras (§`oculto-en-movil`). */}
      <button type="button" className="boton secundario oculto-en-movil" onClick={() => setAbierto(true)}>
        {etiqueta}
      </button>
      {abierto && (
        <DialogoImportarCsv
          ruta={ruta}
          alCerrar={() => setAbierto(false)}
          alTerminar={() => {
            setAbierto(false);
            alTerminar();
          }}
        />
      )}
    </>
  );
}

function DialogoImportarCsv({
  ruta,
  alCerrar,
  alTerminar,
}: {
  ruta: string;
  alCerrar: () => void;
  alTerminar: () => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [nombreArchivo, setNombreArchivo] = useState<string | null>(null);
  const [importacion, setImportacion] = useState<ImportacionDto | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creadas, setCreadas] = useState<number | null>(null);
  const [omitidas, setOmitidas] = useState<OmitidaImportacion[]>([]);
  const [fallos, setFallos] = useState<FalloImportacion[]>([]);
  const [progreso, setProgreso] = useState<ProgresoImportacion | null>(null);
  const [reintentandoFotos, setReintentandoFotos] = useState(false);
  const [fotosReintentadas, setFotosReintentadas] = useState<number | null>(null);
  // Apagado por defecto (§ pedido): borrar la data existente es destructivo,
  // así que hay que activarlo a propósito en cada importación.
  const [limpiarExistentes, setLimpiarExistentes] = useState(false);
  // Cookie de sesión de GetMaintainX para bajar las fotos del export. Vive solo en este diálogo
  // (no en el servidor): vence seguido, así que en vez de editar `GETMAINTAINX_COOKIE` en el
  // `.env` y reiniciar la API cada vez, se pega acá y viaja con esta importación puntual. Si se
  // deja vacío, el servidor cae a la del `.env` si hay una configurada.
  const [cookieGetMaintainX, setCookieGetMaintainX] = useState('');

  // §pedido: un activo se ubica por el *nombre* de su ubicación (columna
  // Location del export), no por ID — si todavía no hay ninguna ubicación
  // creada, cada fila de Activos.csv va a quedar sin ubicación resuelta.
  const { data: ubicaciones, isLoading: cargandoUbicaciones } = useUbicaciones(ruta === 'activos');
  const sinUbicaciones = ruta === 'activos' && !cargandoUbicaciones && (ubicaciones?.length ?? 0) === 0;
  // Las órdenes se importan siempre como nuevas: el servidor rechaza «reemplazar» para ellas.
  const admiteReemplazo = ruta !== 'ordenes';

  async function alElegirArchivo(archivo: File): Promise<void> {
    setError(null);
    setImportacion(null);
    setCreadas(null);
    setOmitidas([]);
    setFotosReintentadas(null);
    setNombreArchivo(archivo.name);
    setCargando(true);
    try {
      const contenido = await archivo.text();
      const resultado = await api.post<ImportacionDto>(`/importaciones/${ruta}/validar`, {
        nombreArchivo: archivo.name,
        contenido,
      });
      setImportacion(resultado);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo leer o validar el archivo.');
    } finally {
      setCargando(false);
    }
  }

  /**
   * Una llamada de confirmar (o de reintentar fotos) puede tardar varios
   * minutos con un CSV grande — es una sola petición que procesa todo antes
   * de responder. Mientras se espera esa respuesta, se consulta aparte el
   * estado de la importación cada 2s solo para leer `progreso`, y así no
   * dejar un spinner ciego todo ese rato.
   */
  function sondearProgreso(id: string): () => void {
    const intervalo = setInterval(() => {
      void api
        .get<{ progreso: ProgresoImportacion | null }>(`/importaciones/${id}`)
        .then((r) => setProgreso(r.progreso))
        .catch(() => undefined);
    }, 2000);
    return () => clearInterval(intervalo);
  }

  /**
   * La confirmación corre en el servidor en segundo plano: el POST responde enseguida
   * (así no hay timeout del proxy). Acá se consulta la importación cada 2s hasta que
   * termine, mostrando el avance. Un fallo de red al consultar no corta la espera.
   */
  async function esperarConfirmacion(id: string): Promise<EstadoConfirmacionDto> {
    for (;;) {
      await new Promise((resolver) => setTimeout(resolver, 2000));
      const estadoActual = await api.get<EstadoConfirmacionDto>(`/importaciones/${id}`).catch(() => null);
      if (!estadoActual) continue;
      setProgreso(estadoActual.progreso);
      if (!estadoActual.confirmando) return estadoActual;
    }
  }

  async function confirmar(): Promise<void> {
    if (!importacion) return;
    setCargando(true);
    setError(null);
    setProgreso(null);

    try {
      await api.post<{ enCurso: true }>(`/importaciones/${importacion.id}/confirmar`, {
        limpiarExistentes: limpiarExistentes && admiteReemplazo,
        cookieGetMaintainX: cookieGetMaintainX.trim() || undefined,
      });
      const final = await esperarConfirmacion(importacion.id);
      if (final.estado === 'CONFIRMADA') {
        setCreadas(final.resultado?.creadas.length ?? 0);
        setOmitidas(final.resultado?.omitidas ?? []);
        setFallos(final.fallos ?? []);
      } else {
        setError(
          final.ultimoError ?? 'La confirmación se interrumpió. Vuelve a confirmar para retomarla desde donde quedó.',
        );
      }
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo confirmar la importación.');
    } finally {
      setProgreso(null);
      setCargando(false);
    }
  }

  async function reintentarFotos(): Promise<void> {
    if (!importacion) return;
    setReintentandoFotos(true);
    setError(null);
    setProgreso(null);
    const detenerSondeo = sondearProgreso(importacion.id);

    try {
      const resultado = await api.post<{ intentadas: number }>(`/importaciones/${importacion.id}/reintentar-fotos`, {
        cookieGetMaintainX: cookieGetMaintainX.trim() || undefined,
      });
      setFotosReintentadas(resultado.intentadas);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudieron reintentar las fotos.');
    } finally {
      detenerSondeo();
      setProgreso(null);
      setReintentandoFotos(false);
    }
  }

  return (
    <Dialogo titulo="Importar CSV" ancho={620} alCerrar={alCerrar}>
      {creadas !== null ? (
        <div>
          <p>
            Listo: se crearon <strong>{creadas}</strong> registros.
          </p>
          {omitidas.length > 0 && (
            <Aviso tono="atencion">
              <p style={{ marginTop: 0 }}>
                <strong>{omitidas.length}</strong> aviso(s) al importar:
              </p>
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {omitidas.map((o, i) => (
                    <li key={i}>
                      Línea {o.linea} ({o.nombre}): {o.motivo}
                    </li>
                  ))}
                </ul>
              </div>
            </Aviso>
          )}
          {fallos.length > 0 && (
            <Aviso tono="error">
              <p style={{ marginTop: 0 }}>
                <strong>{fallos.length}</strong> OT no se importaron o quedaron incompletas:
              </p>
              <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {fallos.map((f, i) => (
                    <li key={i} style={{ marginBottom: 6 }}>
                      <strong>OT {f.idExterno}</strong> · línea {f.linea} · «{f.titulo}»
                      <br />
                      Paso: {PASO_FALLO[f.paso]}
                      <br />
                      Motivo: {f.motivo}
                    </li>
                  ))}
                </ul>
              </div>
              <button type="button" className="boton-secundario" style={{ marginTop: 8 }} onClick={() => void descargarArchivo(`/importaciones/${importacion?.id}/fallos.csv`)}>
                Descargar log de fallos (CSV)
              </button>
            </Aviso>
          )}
          {fotosReintentadas !== null && (
            <Aviso tono="info">Se reintentaron {fotosReintentadas} foto(s).</Aviso>
          )}
          {reintentandoFotos && progreso && (
            <p>
              Reintentando fotos — {progreso.procesadas} de {progreso.total}…
            </p>
          )}
          <label className="campo" style={{ marginTop: 4 }}>
            Cookie de GetMaintainX (pegar una nueva si la anterior ya venció)
            <textarea
              value={cookieGetMaintainX}
              onChange={(e) => setCookieGetMaintainX(e.target.value)}
              rows={2}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </label>
          {error && <span className="campo-error">{error}</span>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            {/* Por si la cookie de GetMaintainX estaba vencida durante la
                confirmación: reintenta solo las fotos, sin volver a crear nada. */}
            {ruta !== 'ordenes' && (
              <button
                type="button"
                className="boton secundario"
                onClick={() => void reintentarFotos()}
                disabled={reintentandoFotos}
              >
                {reintentandoFotos ? 'Reintentando…' : 'Reintentar fotos'}
              </button>
            )}
            <button type="button" className="boton" onClick={alTerminar} disabled={reintentandoFotos}>
              Cerrar
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sinUbicaciones && (
            <Aviso tono="atencion">
              Todavía no hay ninguna ubicación registrada. Se recomienda importar primero el CSV de{' '}
              <strong>Ubicaciones</strong>: los activos se vinculan a su ubicación por nombre, y sin ella
              quedarán sin ubicación asignada.
            </Aviso>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const archivo = e.target.files?.[0];
              if (archivo) void alElegirArchivo(archivo);
            }}
          />

          {cargando && (
            <div>
              {progreso ? (
                <>
                  <p style={{ marginBottom: 4 }}>
                    Importando — {progreso.procesadas} de {progreso.total}…
                  </p>
                  <div
                    style={{
                      height: 6,
                      borderRadius: 3,
                      background: 'var(--borde-suave, #eee)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${progreso.total > 0 ? Math.round((progreso.procesadas / progreso.total) * 100) : 0}%`,
                        background: 'var(--azul)',
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </div>
                </>
              ) : (
                <p>Procesando…</p>
              )}
            </div>
          )}
          {error && <span className="campo-error">{error}</span>}

          {importacion && !cargando && (
            <div>
              <p>
                <strong>{nombreArchivo}</strong> — {importacion.filasValidas} de {importacion.filasTotales} filas
                válidas
                {importacion.estado === 'FALLIDA' && ' (no se puede importar así)'}.
              </p>

              {importacion.errores.length > 0 && (
                <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--borde, #ddd)', borderRadius: 6, padding: 8 }}>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {importacion.errores.map((err, i) => (
                      <li key={i}>
                        Línea {err.linea} ({err.columna}): {err.mensaje}
                        {err.valor ? ` — «${err.valor}»` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {importacion.estado === 'VALIDADA' && (
                <>
                  <label className="campo" style={{ marginTop: 12 }}>
                    Cookie de GetMaintainX (opcional, para traer las fotos)
                    <textarea
                      value={cookieGetMaintainX}
                      onChange={(e) => setCookieGetMaintainX(e.target.value)}
                      placeholder="Pegar acá la cookie si la del .env ya venció"
                      rows={2}
                      style={{ fontFamily: 'monospace', fontSize: 12 }}
                    />
                  </label>
                  {admiteReemplazo && (
                    <label className="campo campo-checkbox" style={{ marginTop: 12 }}>
                      <input
                        type="checkbox"
                        checked={limpiarExistentes}
                        onChange={(e) => setLimpiarExistentes(e.target.checked)}
                      />
                      Reemplazar datos existentes (borra lo que ya haya antes de importar)
                    </label>
                  )}
                  {limpiarExistentes && admiteReemplazo && (
                    <Aviso tono="error">
                      {ruta === 'ubicaciones'
                        ? 'Esto eliminará TODAS las ubicaciones existentes y también TODOS los activos que dependen de ellas, antes de crear lo que trae este archivo. No se puede deshacer.'
                        : ruta === 'activos'
                          ? 'Esto eliminará TODOS los activos existentes antes de crear lo que trae este archivo. No se puede deshacer.'
                          : ruta === 'partes'
                            ? 'Esto eliminará (borrado lógico) TODAS las partes existentes antes de crear las que trae este archivo. Su historial de inventario se conserva.'
                            : 'Esto eliminará TODAS las plantillas de procedimiento existentes antes de crear las que trae este archivo. No se puede deshacer.'}
                    </Aviso>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                    <button type="button" className="boton secundario" onClick={alCerrar}>
                      Cancelar
                    </button>
                    <button type="button" className="boton" onClick={() => void confirmar()} disabled={cargando}>
                      Confirmar e importar {importacion.filasValidas} filas
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Dialogo>
  );
}
